import { useState, useEffect, memo } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Genus } from '../../types/plant';
import { plantApi } from '../../lib/plantApi';
import { Button, ComboBox } from '../ui';
import type { ComboBoxOption } from '../ui';
import { useSearchInput } from '../../hooks/useSearchInput';
import { useDebounce } from '../../hooks/useDebounce';

interface GenusSearchProps {
  value?: Genus | null;
  onChange: (genus: Genus | null) => void;
  error?: string;
  placeholder?: string;
  onCreateNew?: (genusName: string) => void;
}

function GenusSearchComponent({
  value,
  onChange,
  error,
  placeholder = 'Search for a genus...',
  onCreateNew,
}: GenusSearchProps) {
  const [genera, setGenera] = useState<Genus[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const { searchValue: searchQuery, handleChange: handleSearchChange } = useSearchInput({
    onSearchChange: () => {}, // We handle the search in the useEffect below
    debounceMs: 300,
  });

  // Use debounced search query
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Search genera when debounced query changes
  useEffect(() => {
    if (debouncedSearchQuery.trim().length >= 1) {
      setIsSearching(true);
      plantApi
        .getGenera({ name: debouncedSearchQuery.trim(), limit: 20 })
        .then(genera => {
          setGenera(genera || []);
        })
        .catch(() => {
          setGenera([]);
        })
        .finally(() => {
          setIsSearching(false);
        });
    } else {
      setGenera([]);
      setIsSearching(false);
    }
  }, [debouncedSearchQuery]);

  // Convert genera to ComboBox options
  const allGenera = [...genera];

  // Add the selected value if it's not already in the genera array
  if (value && !genera.some(g => g.id === value.id)) {
    allGenera.unshift(value);
  }

  const genusOptions: ComboBoxOption<Genus>[] = allGenera.map(genus => ({
    value: genus,
    label: genus.name,
  }));

  const renderGenusOption = (option: ComboBoxOption<Genus>) => {
    const genus = option.value;
    return (
      <div className="flex flex-col">
        <span className="font-medium text-foreground italic">{genus.name}</span>
      </div>
    );
  };

  const handleCreateNew = () => {
    if (onCreateNew && searchQuery.trim()) {
      onCreateNew(searchQuery.trim());
    }
  };

  const dropdownFooter =
    searchQuery.trim().length >= 1 && onCreateNew ? (
      <div className="px-3 py-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleCreateNew}
          className="w-full justify-start text-green-600 hover:text-green-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Create genus "{searchQuery.trim()}"
        </Button>
      </div>
    ) : null;

  return (
    <ComboBox
      value={value}
      onChange={onChange}
      options={genusOptions}
      placeholder={placeholder}
      error={error}
      searchable
      onSearch={handleSearchChange}
      searchQuery={searchQuery}
      loading={isSearching}
      emptyMessage={
        searchQuery.trim().length >= 1
          ? `No genera found for "${searchQuery}"`
          : 'Type to search genera'
      }
      renderOption={renderGenusOption}
      dropdownFooter={dropdownFooter}
      getOptionKey={(genus: Genus) => genus.id}
    />
  );
}

export const GenusSearch = memo(GenusSearchComponent);
